import React, { ReactNode, useEffect } from 'react';
import { Modal, StyleSheet, TouchableWithoutFeedback } from 'react-native';

import { SimpleHeader } from '@components/Headers/SimpleHeader.web';
import { Box, ShadowBox } from '@components/Restyle';
import theme from '@themes/theme';

type FinalWebModalProps = {
  accessibilityLabel: string;
  visible: boolean;
  children: ReactNode;
  onClose?: () => void;
  height?: number;
  width?: number;
  title?: string;
  canOverflowModal?: boolean;
  isFullScreen?: boolean;
  titleVariant?: keyof typeof theme.textVariants;
  titleColor?: keyof typeof theme.colors;
  marginHorizontal?: keyof typeof theme.spacing;
  marginTop?: keyof typeof theme.spacing;
};

export const WebModalV3 = ({
  accessibilityLabel,
  visible,
  onClose,
  children,
  width,
  title,
  isFullScreen = false,
  titleVariant,
  titleColor,
  marginHorizontal,
  marginTop,
  canOverflowModal = false,
}: FinalWebModalProps) => {
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;

    if (visible) {
      // Prevent scrolling of the page behind the modal
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = originalOverflow || '';
    }

    // Restore on unmount
    return () => {
      document.body.style.overflow = originalOverflow || '';
    };
  }, [visible]);

  return (
    <Modal animationType='fade' transparent={true} visible={visible}>
      <Box
        accessibilityLabel={
          accessibilityLabel ? accessibilityLabel + ' Screen' : 'Modal'
        }
        style={styles.modalContainer}>
        <TouchableWithoutFeedback onPress={onClose} style={styles.modal}>
          <Box style={styles.modal}></Box>
        </TouchableWithoutFeedback>

        <ShadowBox
          variant='thin'
          style={[styles.popUp]}
          borderRadius={isFullScreen ? 'none' : 'm'}
          overflow={canOverflowModal ? 'visible' : 'hidden'}
          width={width}>
          <>
            {!!title && (
              <SimpleHeader
                title={title}
                onClose={onClose}
                titleVariant={titleVariant}
                titleColor={titleColor}
                marginHorizontal={marginHorizontal}
                marginTop={marginTop}
              />
            )}
            {children}
          </>
        </ShadowBox>
      </Box>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modal: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'black',
    opacity: 0.5,
  },
  modalContainer: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  popUp: {
    position: 'absolute',
    backgroundColor: 'white',
  },
});
